import { Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { useWavePlanParameters } from '@/hooks/useWavePlans'
import type { WaveParamGroup } from '@/api/wavePlans'

const GROUP_LABELS: Record<WaveParamGroup, string> = {
  WP_SEASONAL: 'WP Seasonal',
  WP_CORE: 'WP Core',
  WP_GENERAL: 'WP General',
}
const GROUP_ORDER: WaveParamGroup[] = ['WP_SEASONAL', 'WP_CORE', 'WP_GENERAL']

// Fractions shown as percentages; everything else as entered.
const PERCENT_KEYS = new Set([
  'first_wave.buffer',
  'recommendation.variance_threshold',
])

function formatValue(key: string, value: unknown) {
  if (PERCENT_KEYS.has(key) && typeof value === 'number') {
    return `${Number((value * 100).toFixed(2))}%`
  }
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (value === null || value === undefined || value === '') return '—'
  return String(value).replace(/_/g, ' ')
}

interface Props {
  planId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onCloseAutoFocus: (event: Event) => void
}

/** The information drawer: the active parameters behind the plan's numbers. */
export function WavePlanParametersDrawer({
  planId,
  open,
  onOpenChange,
  onCloseAutoFocus,
}: Props) {
  const parameters = useWavePlanParameters(open ? planId : undefined)
  const data = parameters.data
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <SheetHeader className="border-b p-4 pr-10">
          <SheetTitle>Calculation parameters</SheetTitle>
          <SheetDescription className="text-xs leading-relaxed">
            {data?.frozen
              ? 'Values frozen with this plan’s submission.'
              : 'Today’s values. Administrator changes apply to every SKU without SKU-by-SKU updates.'}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          {parameters.isPending && (
            <p
              role="status"
              className="flex items-center gap-2 text-xs text-muted-foreground"
            >
              <Loader2 size={14} className="animate-spin" /> Loading…
            </p>
          )}
          {parameters.isError && (
            <div role="alert" className="space-y-2 text-xs">
              <p className="text-destructive">
                The parameters could not be loaded.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void parameters.refetch()}
              >
                Retry
              </Button>
            </div>
          )}
          {data &&
            GROUP_ORDER.map(group => {
              const items = data.parameters.filter(item => item.group === group)
              if (!items.length) return null
              return (
                <section key={group} aria-labelledby={`params-${group}`}>
                  <h3
                    id={`params-${group}`}
                    className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                  >
                    {GROUP_LABELS[group]}
                  </h3>
                  <dl className="divide-y rounded-md border">
                    {items.map(item => (
                      <div
                        key={item.key}
                        className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-3 py-2"
                      >
                        <dt className="min-w-0 text-xs">
                          <span className="block font-medium break-words">
                            {item.description}
                          </span>
                          <span className="block font-mono text-[11px] text-muted-foreground">
                            {item.key}
                          </span>
                        </dt>
                        <dd className="text-right text-xs tabular-nums">
                          <span className="font-semibold">
                            {formatValue(item.key, item.value)}
                          </span>
                          {!item.is_default && (
                            <span className="block text-[11px] text-muted-foreground">
                              default {formatValue(item.key, item.default)}
                            </span>
                          )}
                          {item.scoped_overrides > 0 && (
                            <span className="block text-[11px] text-muted-foreground">
                              + {item.scoped_overrides} vendor/SKU value
                              {item.scoped_overrides === 1 ? '' : 's'}
                            </span>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )
            })}
        </div>
      </SheetContent>
    </Sheet>
  )
}
